import { useState, useMemo, useRef } from 'react'
import { Checkbox, Icon, SlimButton, Tooltip } from 'components'

import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LibraryAddCheckRoundedIcon from '@mui/icons-material/LibraryAddCheckRounded'

import * as S from './styles'

type OptionType = {
  label: string
  value: string
}

export type MultiselectMenuProps = {
  label: string
  searchPlaceholder?: string
  optionsList: OptionType[]
  selectedOptions: string[]
  onChange: (options: string[]) => void
  hasSearch?: boolean
  clearAction?: () => void
  checkAllAction?: () => void
  buttonAction?: () => void
  buttonActionLabel?: string
  required?: boolean
  disabled?: boolean
  disableOptions?: boolean
  hasBorder?: boolean
  fullWidth?: boolean
}

export const MultiselectMenu: React.FC<MultiselectMenuProps> = ({
  label,
  searchPlaceholder = 'Buscar',
  optionsList,
  selectedOptions,
  onChange,
  hasSearch = false,
  clearAction = undefined,
  checkAllAction = undefined,
  buttonAction = undefined,
  buttonActionLabel = 'Buscar',
  required = false,
  disabled,
  disableOptions = false,
  hasBorder = true,
  fullWidth = false
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setSearchValue('')
    }, 200)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'multiselect-popover' : undefined
  const requiredError = required && selectedOptions.length === 0
  const hasActions =
    clearAction !== undefined ||
    checkAllAction !== undefined ||
    buttonAction !== undefined ||
    requiredError

  const handleSelect = (option: string) => {
    if (selectedOptions.includes(option)) {
      onChange(selectedOptions.filter((item) => item !== option))
    } else {
      onChange([...selectedOptions, option])
    }
  }

  const handleClear = () => {
    setSearchValue('')
    if (clearAction !== undefined) clearAction()
  }

  const handleCheckAll = () => {
    if (checkAllAction !== undefined) checkAllAction()
  }

  const isOptionSelected = (option: string) => {
    return selectedOptions.includes(option)
  }

  const options = useMemo(() => {
    if (searchValue === '') return optionsList
    return optionsList.filter((opt) =>
      opt.label.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
    )
  }, [searchValue, optionsList])

  const getSelectedOptions = () => {
    const optionLabel = optionsList
      .find((opt) => opt.value === selectedOptions[0])
      ?.label.split(' - ')[0]
    if (selectedOptions.length === 0 || selectedOptions[0] === '') return ''
    if (selectedOptions.length === 1) return ` • ${optionLabel}`
    if (selectedOptions.length === optionsList.length) return ` • Todos`
    return ` • ${optionLabel} (+${selectedOptions.length - 1})`
  }

  const focusInput = () => {
    inputRef.current?.focus()
  }

  return (
    <S.Wrapper fullWidth={fullWidth}>
      <S.StyledButton
        aria-describedby={id}
        open={open}
        hasBorder={hasBorder}
        variant="contained"
        onClick={handleClick}
        disableElevation
        endIcon={<KeyboardArrowDownIcon color="primary" />}
        disabled={disabled}
        required={requiredError}
      >
        {label}
        {getSelectedOptions()}
      </S.StyledButton>
      <S.StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        elevation={0}
      >
        <S.PopoverContent>
          {hasSearch && (
            <S.InputWrapper>
              <S.InputContainer onClick={focusInput}>
                <input
                  ref={inputRef}
                  value={searchValue}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <SearchIcon fontSize="small" />
              </S.InputContainer>
            </S.InputWrapper>
          )}
          <S.OptionsWrapper data-testid="multiselect-menu-options">
            {options.length ? (
              options.map((option, i) => (
                <S.OptionRow
                  key={`multiselect-option-${option.value}-${i}`}
                  data-testid={`option-row-${option.value}-${i}`}
                >
                  <Checkbox
                    size="small"
                    checked={isOptionSelected(option.value)}
                    onChange={() => handleSelect(option.value)}
                    disabled={disableOptions}
                  />
                  <S.OptionText>{option.label}</S.OptionText>
                </S.OptionRow>
              ))
            ) : (
              <S.OptionRow key={`multiselect-menu-option-not-found`}>
                <S.OptionText>
                  <strong>Nenhuma opção encontrada.</strong>
                </S.OptionText>
              </S.OptionRow>
            )}
          </S.OptionsWrapper>
          {requiredError && (
            <S.ActionsWrapper>
              <S.ErrorText>Ao menos uma opção deve estar selecionada.</S.ErrorText>
            </S.ActionsWrapper>
          )}
          {hasActions && (
            <S.ActionsWrapper>
              {checkAllAction && (
                <Tooltip title="Selecionar todos" placement="top" arrow>
                  <div>
                    <SlimButton
                      id="multiselect-menu-check-all-button"
                      variant="secondary"
                      onClick={handleCheckAll}
                      type="button"
                      icon={<LibraryAddCheckRoundedIcon fontSize="small" />}
                      disabled={disableOptions}
                    />
                  </div>
                </Tooltip>
              )}
              {clearAction && (
                <Tooltip title="Limpar selecionados" placement="top" arrow>
                  <div>
                    <SlimButton
                      id="multiselect-menu-clear-button"
                      variant="secondary"
                      onClick={handleClear}
                      type="button"
                      icon={<Icon icon="clear-all" size="20" />}
                      disabled={disableOptions}
                    />
                  </div>
                </Tooltip>
              )}
              {buttonAction && (
                <SlimButton
                  id="multiselect-menu-search-button"
                  variant="primary"
                  onClick={buttonAction}
                  type="button"
                  content={buttonActionLabel}
                  disabled={disableOptions}
                />
              )}
            </S.ActionsWrapper>
          )}
        </S.PopoverContent>
      </S.StyledPopover>
    </S.Wrapper>
  )
}
